import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Package,
  QrCode,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import {
  useReceiveReturnItem,
  useReturnRequest,
  useReturnRequestItems,
  useUpdateReturnRequestStatus,
} from "~/hooks/wms";
import { useProfileStore } from "~/store/profileStore";
import type { ReturnRequestItem } from "~/types/returns";
import {
  RETURN_ITEM_STATUS_LABELS,
  RETURN_REASON_LABELS,
} from "~/types/returns";

export function ReturnReceivingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: userId } = useProfileStore();

  const [selectedItem, setSelectedItem] = useState<ReturnRequestItem | null>(
    null
  );
  const [receivedQuantity, setReceivedQuantity] = useState<number>(0);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);

  const { data: returnRequest, isLoading: requestLoading } =
    useReturnRequest(id);
  const { data: items = [], isLoading: itemsLoading } =
    useReturnRequestItems(id);
  const receiveMutation = useReceiveReturnItem();
  const updateStatusMutation = useUpdateReturnRequestStatus();

  const isLoading = requestLoading || itemsLoading;

  const pendingItems = items.filter((item) => item.status === "pending");
  const receivedItems = items.filter((item) => item.status !== "pending");
  const progress =
    items.length > 0 ? (receivedItems.length / items.length) * 100 : 0;

  const handleReceiveItem = (item: ReturnRequestItem) => {
    setSelectedItem(item);
    setReceivedQuantity(item.expected_quantity);
    setReceiveDialogOpen(true);
  };

  const confirmReceive = () => {
    if (!selectedItem || !userId) return;

    receiveMutation.mutate(
      {
        formData: {
          return_request_item_id: selectedItem.id,
          received_quantity: receivedQuantity,
        },
        userId,
      },
      {
        onSuccess: () => {
          toast({
            title: "Item recibido",
            description: `${selectedItem.product?.short_name || "Producto"} marcado como recibido`,
          });
          setReceiveDialogOpen(false);
          setSelectedItem(null);

          // Check if all items are received
          const remainingPending = pendingItems.filter(
            (i) => i.id !== selectedItem.id
          ).length;

          if (remainingPending === 0 && id) {
            // All items received, update status to inspecting
            updateStatusMutation.mutate(
              { id, status: "inspecting" },
              {
                onSuccess: () => {
                  toast({
                    title: "Recepción completada",
                    description:
                      "Todos los items han sido recibidos. Continuando a inspección.",
                  });
                  navigate(`/wms/returns/${id}/inspect`);
                },
              }
            );
          }
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: `No se pudo registrar la recepción: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleContinueToInspection = () => {
    if (!id) return;

    updateStatusMutation.mutate(
      { id, status: "inspecting" },
      {
        onSuccess: () => {
          navigate(`/wms/returns/${id}/inspect`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (!returnRequest) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
            <p className="text-muted-foreground">Devolución no encontrada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/wms/returns")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Devoluciones
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Recepción de Devolución"
        description={`${returnRequest.return_number} - ${returnRequest.client?.name || "Sin cliente"}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms/returns")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-2xl space-y-6">
        {/* Progress Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Progreso de recepción
                </span>
                <span className="font-medium">
                  {receivedItems.length} / {items.length} items
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Pending Items */}
        {pendingItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4" />
                Items por Recibir ({pendingItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="space-y-1">
                    <p className="font-medium">
                      {item.product?.short_name || "Producto"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {RETURN_REASON_LABELS[item.return_reason]}
                    </p>
                    <Badge variant="secondary">
                      {item.expected_quantity} unidades esperadas
                    </Badge>
                  </div>
                  <Button onClick={() => handleReceiveItem(item)}>
                    <QrCode className="mr-2 h-4 w-4" />
                    Recibir
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Received Items */}
        {receivedItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Items Recibidos ({receivedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {receivedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4"
                >
                  <div className="space-y-1">
                    <p className="font-medium">
                      {item.product?.short_name || "Producto"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {RETURN_REASON_LABELS[item.return_reason]}
                    </p>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="border-green-300">
                        {item.received_quantity} / {item.expected_quantity}{" "}
                        recibidas
                      </Badge>
                      {item.received_quantity !== item.expected_quantity && (
                        <Badge variant="destructive">Discrepancia</Badge>
                      )}
                    </div>
                  </div>
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Continue Button */}
        {receivedItems.length > 0 && pendingItems.length === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium">Recepción Completada</p>
                    <p className="text-sm text-muted-foreground">
                      Todos los items han sido recibidos
                    </p>
                  </div>
                </div>
                <Button onClick={handleContinueToInspection}>
                  Continuar a Inspección
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Receive Dialog */}
        <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recibir Producto</DialogTitle>
              <DialogDescription>
                Confirme la cantidad recibida para este producto
              </DialogDescription>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 py-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Producto
                  </Label>
                  <p className="font-medium">
                    {selectedItem.product?.short_name || "Producto"}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Razón de Devolución
                  </Label>
                  <p className="text-sm">
                    {RETURN_REASON_LABELS[selectedItem.return_reason]}
                  </p>
                </div>
                {selectedItem.return_reason_notes && (
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Notas
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {selectedItem.return_reason_notes}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Cantidad Esperada
                    </Label>
                    <p className="text-2xl font-bold">
                      {selectedItem.expected_quantity}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="receivedQuantity">Cantidad Recibida</Label>
                    <Input
                      id="receivedQuantity"
                      type="number"
                      min={0}
                      max={selectedItem.expected_quantity * 2}
                      value={receivedQuantity}
                      onChange={(e) =>
                        setReceivedQuantity(Number(e.target.value))
                      }
                      className="text-lg"
                    />
                  </div>
                </div>
                {receivedQuantity !== selectedItem.expected_quantity && (
                  <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <p className="text-sm text-yellow-700">
                      La cantidad recibida difiere de la esperada. Se registrará
                      una discrepancia.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReceiveDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmReceive}
                disabled={receiveMutation.isPending || receivedQuantity < 0}
              >
                {receiveMutation.isPending
                  ? "Guardando..."
                  : "Confirmar Recepción"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}

export default ReturnReceivingPage;
